import React, { useEffect, useState } from "react";
import axiosInstance from "../../../api/axiosInstance";
import SubnetDetailModal from "./SubnetDetailModal";
import DarkModeSelect from "../../../components/DarkModeSelect";
import TabList from "./TabList";

// Button Component
function Button({ variant = "default", children, className = "", ...props }) {
    const base =
        "px-6 py-3 rounded-lg font-medium text-sm transition-all duration-200 flex items-center gap-2";
    const variants = {
        default:
            "bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg hover:shadow-xl",
        secondary:
            "bg-gray-700 text-gray-200 hover:bg-gray-600 border border-gray-600",
        danger: "bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white shadow-lg",
        success:
            "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-lg",
    };
    return (
        <button
            className={`${base} ${variants[variant]} ${className}`}
            {...props}
        >
            {children}
        </button>
    );
}

// Card Component
function Card({ children, className = "" }) {
    return (
        <div
            className={`bg-gray-800/50 backdrop-blur-lg border border-gray-700 rounded-br-2xl rounded-bl-2xl shadow-xl hover:shadow-2xl transition-all duration-300 ${className}`}
        >
            {children}
        </div>
    );
}

export function Tabs({ defaultValue, children }) {
    const [active, setActive] = useState(defaultValue);
    return (
        <div>
            {React.Children.map(children, (child) =>
                React.cloneElement(child, { active, setActive })
            )}
        </div>
    );
}

// Main Subnet Management Component
export default function SubnetManagement() {
    const [subnets, setSubnets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedSubnet, setSelectedSubnet] = useState(null);
    const [showCreatePopup, setShowCreatePopup] = useState(false);
    const [networks, setNetworks] = useState([]);
    const [selectedNetworkId, setSelectedNetworkId] = useState({});
    const [name, setName] = useState("");
    const [networkAddress, setNetworkAddress] = useState("");
    const [ipVersion, setIpVersion] = useState(4);
    const [gatewayIp, setGatewayIp] = useState("");
    const [enableDhcp, setEnableDhcp] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(
                "openstack/network/subnets/"
            );
            const data = response.data.map((subnet) => ({
                ...subnet,
                network_name: "Unknown", // Placeholder until real name is fetched from backend
                status: "active", // Default status
                description: "", // Optional if not used in UI
            }));
            setSubnets(data);
        } catch (error) {
            console.error("Failed to fetch subnets:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (!showCreatePopup) return;

        const fetcNetworks = async () => {
            try {
                const response = await axiosInstance.get(
                    "openstack/network/network-list/"
                );
                const formattedNetworks = response.data.map((network) => ({
                    value: network.id,
                    label: network.name,
                }));
                setNetworks(formattedNetworks);
            } catch (err) {
                console.error("❌ Failed to fetch networks:", err);
            }
        };
        fetcNetworks();
    }, [showCreatePopup]);

    const handleCreate = async () => {
        try {
            await axiosInstance.post("/openstack/network/subnets/", {
                name: name,
                network_id: selectedNetworkId.value,
                cidr: networkAddress,
                ip_version: ipVersion,
                gateway_ip: gatewayIp,
                enable_dhcp: enableDhcp,
            });
            alert("✅ User assigned successfully!");
            fetchData();
            setShowCreatePopup(false);
        } catch (err) {
            alert("❌ Failed to create subnet.");
        }
    };

    return (
        <div className="min-h-screen text-white">
           
            <main className="container mx-auto px-4 py-6">
                <section className="mx-auto py-5">
                    <header className="border-b border-gray-700 bg-gray-900/50 backdrop-blur-lg">
                        <div className="p-6 lg:px-8">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                                        <span className="text-2xl">🔗</span>
                                    </div>
                                    <div>
                                        <h1 className="text-3xl font-bold bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
                                            Subnet Management
                                        </h1>
                                        <p className="text-gray-400 mt-1">
                                            Configure and manage network subnets
                                        </p>
                                    </div>
                                </div>
                                <Button
                                    onClick={() => setShowCreatePopup(true)}
                                >
                                    ➕ Create Subnet
                                </Button>
                            </div>
                        </div>
                    </header>

                    <div className="py-8">
                        <TabList />
                        <Card>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-700">
                                    <thead className="bg-gray-900/50">
                                        <tr>
                                            {[
                                                "Subnet Name",
                                                "CIDR",
                                                "Gateway",
                                                "DHCP",
                                                "Actions",
                                            ].map((label, idx) => (
                                                <th
                                                    key={idx}
                                                    className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                                                >
                                                    {label}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-800">
                                        {loading ? (
                                            <tr>
                                                <td
                                                    colSpan="5"
                                                    className="text-center py-12"
                                                >
                                                    <div className="flex items-center justify-center gap-3">
                                                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-500"></div>
                                                        <span className="text-gray-400">
                                                            Loading subnets...
                                                        </span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : subnets.length === 0 ? (
                                            <tr>
                                                <td
                                                    colSpan="5"
                                                    className="text-center py-12"
                                                >
                                                    <div className="text-gray-500">
                                                        <div className="text-4xl mb-4">
                                                            🔗
                                                        </div>
                                                        <p className="font-medium">
                                                            No subnets found
                                                        </p>
                                                        <p className="text-sm text-gray-600 mt-1">
                                                            Create your first
                                                            subnet to get
                                                            started
                                                        </p>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            subnets.map((subnet) => (
                                                <tr
                                                    key={subnet.id}
                                                    className="hover:bg-gray-800/30 transition-colors duration-200"
                                                >
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center">
                                                                <span className="text-green-400 text-sm">
                                                                    🔗
                                                                </span>
                                                            </div>
                                                            <div>
                                                                <span className="font-medium text-gray-200">
                                                                    {
                                                                        subnet.name
                                                                    }
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <code className="text-sm text-gray-300 bg-gray-900/50 px-2 py-1 rounded">
                                                            {subnet.cidr}
                                                        </code>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <code className="text-sm text-gray-300">
                                                            {subnet.gateway_ip}
                                                        </code>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span
                                                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                                subnet.enable_dhcp
                                                                    ? "bg-green-500/20 text-green-400"
                                                                    : "bg-gray-500/20 text-gray-400"
                                                            }`}
                                                        >
                                                            {subnet.enable_dhcp
                                                                ? "● Enabled"
                                                                : "○ Disabled"}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex gap-3">
                                                            <button
                                                                className="text-blue-400 hover:text-blue-300 text-sm"
                                                                onClick={() =>
                                                                    setSelectedSubnet(
                                                                        subnet
                                                                    )
                                                                }
                                                            >
                                                                View
                                                            </button>
                                                            <button className="text-red-400 hover:text-red-300 text-sm">
                                                                Delete
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    </div>
                    {selectedSubnet && (
                        <SubnetDetailModal
                            subnet={selectedSubnet}
                            onClose={() => setSelectedSubnet(null)}
                        />
                    )}
                    {showCreatePopup && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                            <div className="bg-gray-900 rounded-xl p-6 w-full max-w-md text-white border border-gray-700 shadow-2xl">
                                <h2 className="text-xl font-semibold mb-4 text-indigo-400">
                                    Create new Subnet
                                </h2>

                                <div className="mb-4">
                                    <label className="block text-sm mb-1">
                                        Network
                                    </label>
                                    <DarkModeSelect
                                        options={networks}
                                        value={selectedNetworkId}
                                        onChange={setSelectedNetworkId}
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="block text-sm mb-1">
                                        Name
                                    </label>
                                    <input
                                        type="text"
                                        value={name}
                                        onChange={(e) =>
                                            setName(e.target.value)
                                        }
                                        className="w-full p-2 rounded bg-gray-800 border border-gray-600 text-white mb-2"
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="block text-sm mb-1">
                                        Network Address (CIDR)
                                    </label>
                                    <input
                                        type="text"
                                        value={networkAddress}
                                        onChange={(e) =>
                                            setNetworkAddress(e.target.value)
                                        }
                                        placeholder="e.g. 192.168.0.0/24, 2001:DB8::/48"
                                        className="w-full p-2 rounded bg-gray-800 border border-gray-600 text-white mb-2"
                                        required
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="block text-sm mb-1">
                                        IP Version
                                    </label>
                                    <select
                                        value={ipVersion}
                                        onChange={(e) =>
                                            setIpVersion(e.target.value)
                                        }
                                        className="w-full p-2 rounded bg-gray-800 border border-gray-600 text-white"
                                    >
                                        <option value="4">IPv4</option>
                                        <option value="6">IPv6</option>
                                    </select>
                                </div>
                                <div className="mb-4">
                                    <label className="block text-sm mb-1">
                                        Gateway IP
                                    </label>
                                    <input
                                        type="text"
                                        value={gatewayIp}
                                        onChange={(e) =>
                                            setGatewayIp(e.target.value)
                                        }
                                        className="w-full p-2 rounded bg-gray-800 border border-gray-600 text-white mb-2"
                                    />
                                </div>
                                <div className="mb-6 flex gap-3 align-middle">
                                    <input
                                        id="enable_dhcp"
                                        type="checkbox"
                                        value={enableDhcp}
                                        onChange={() =>
                                            setEnableDhcp(!enableDhcp)
                                        }
                                    />
                                    <label
                                        htmlFor="enable_dhcp"
                                        className="block text-sm"
                                    >
                                        Enable DHCP
                                    </label>
                                </div>

                                <div className="flex justify-around gap-4">
                                    <button
                                        onClick={() =>
                                            setShowCreatePopup(false)
                                        }
                                        className="px-4 py-2 rounded bg-gray-700 hover:bg-gray-600"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleCreate}
                                        className="px-4 py-2 rounded bg-green-600 hover:bg-green-500 text-white font-semibold"
                                        disabled={
                                            !selectedNetworkId ||
                                            !name ||
                                            !networkAddress ||
                                            !ipVersion
                                        }
                                    >
                                        Create
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </section>
            </main>
        </div>
    );
}
